import React from "react";
import PdfButton from "../../ui/PdfButton.jsx";
import { fulfillmentText, laneLines, lanePallets, notReadyText, palletName } from "../../../features/shippingHub/soBoard";
import { chipClass, loadDriverText, loadVehicleText, loadedText, signChips } from "../../../features/signing/signWords";

// One order in a Shipping lane on a phone: the desktop hub's row (features/shippingHub/ShippingHub.jsx) as a card,
// its ONE big button full width. `view` = hubActions.laneRowView. Layout only.
export default function OrderCard({ order: o, lane, view, busy, disabled, manifest, onPrimary, onShowQr, onOpenGuide }) {
  const { due, act, outInfo, qrPending, needCode } = view;
  const primaryClass = act.kind === "pickup" ? "btn-success" : lane === "ready" ? "btn-primary" : "btn-outline-primary";
  return (
    <div className="jq-card" data-testid="phone-ship-order">
      <div className="d-flex justify-content-between align-items-start gap-2">
        <div>
          <div className="fw-bold" style={{ fontSize: "1.2rem" }}>{o.order_number}</div>
          <div>{o.customer_name}{o.customer_short_code ? <span className="jq-muted"> · {o.customer_short_code}</span> : null}</div>
          {o.customer_po && <div className="small jq-muted">PO {o.customer_po}</div>}
        </div>
        <span className="jq-tag">{fulfillmentText(o.fulfillment_type)}</span>
      </div>
      <div className={`small mt-1 ${due.late ? "text-danger fw-semibold" : "jq-muted"}`}>{due.text}</div>
      <div className="mt-1">
        {laneLines(o, lane).map((l) => <div key={l}>{l}</div>)}
      </div>
      {outInfo && <OutLoad order={o} />}
      <div className="d-grid gap-2 mt-2">
        {qrPending || needCode ? (
          <button type="button" className="btn btn-primary btn-lg" disabled={disabled} onClick={onShowQr}
            data-testid={needCode ? "phone-ship-start-delivery-qr" : "phone-ship-show-qr"}>
            {busy && <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />}Show signing QR
          </button>
        ) : (
          <button type="button" className={`btn btn-lg ${primaryClass}`} disabled={disabled} onClick={onPrimary} data-testid="phone-ship-primary">
            {busy && <span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />}{act.label}
          </button>
        )}
        {outInfo && manifest && (
          <PdfButton url={manifest.url} filename={manifest.filename} label="Manifest" className="btn btn-outline-secondary w-100"
            testId={`phone-ship-manifest-${o.plan_id}`} />
        )}
        {(act.kind === "pickup" || act.kind === "load" || qrPending || needCode) && (
          <button type="button" className="btn btn-link" onClick={onOpenGuide}>Open package guide</button>
        )}
      </div>
    </div>
  );
}

/** Out for delivery: driver / truck, loaded when and by whom, the signature chips. */
function OutLoad({ order }) {
  const load = order.load || null;
  const who = [loadDriverText(load), loadVehicleText(load)].filter(Boolean).join(" · ");
  const loaded = loadedText(load);
  const chips = signChips(order.sign, { deliveredAt: order.delivered_at || null });
  return (
    <div className="mt-2 small" data-testid="phone-ship-out-load">
      {who && <div><span className="jq-muted">Driver / truck:</span> {who}</div>}
      {loaded && <div><span className="jq-muted">Loaded:</span> {loaded}</div>}
      {chips.length > 0 && (
        <div className="d-flex flex-wrap gap-1 mt-1">
          {chips.map((c) => <span key={c.text} className={chipClass(c.tone)}>{c.text}</span>)}
          {order.sign && order.sign.status === "pending" && <span className="jq-muted ms-1">QR on the manifest</span>}
        </div>
      )}
    </div>
  );
}

/** Under Pack, collapsed: an order whose pallets can't be packed yet, each pallet's reason. */
export function NotReadyCard({ order: o, due, disabled, onOpen, onOutsourcing }) {
  const ps = lanePallets(o, "not_ready");
  return (
    <div className="jq-card jq-muted" data-testid="phone-ship-not-ready">
      <div className="d-flex justify-content-between align-items-start gap-2">
        <div>
          <div className="fw-bold">{o.order_number}</div>
          <div>{o.customer_name}{o.customer_short_code ? <span> · {o.customer_short_code}</span> : null}</div>
        </div>
        <span className="jq-tag">{fulfillmentText(o.fulfillment_type)}</span>
      </div>
      <div className={`small mt-1 ${due.late ? "text-danger fw-semibold" : ""}`}>{due.text}</div>
      {laneLines(o, "not_ready").map((l) => <div key={l}>{l}</div>)}
      {ps.map((p) => {
        const why = notReadyText(p);
        return (
          <div key={p.pallet_id}>
            <span className="fw-semibold">{palletName(p)}</span>: {why.text}
            {why.outsourcing && (
              <button type="button" className="btn btn-link p-0 ms-2 align-baseline" onClick={onOutsourcing}>Go to Outsourcing</button>
            )}
          </div>
        );
      })}
      <button type="button" className="btn btn-outline-secondary w-100 mt-2" disabled={disabled} onClick={() => onOpen(ps[0] ? ps[0].pallet_id : null)}>
        Open
      </button>
    </div>
  );
}
